import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, UtensilsCrossed, Clock, Users, Image as ImageIcon } from 'lucide-react';
import * as planService from '../../../services/planService';

/**
 * Recipe Library Component
 * Recipe browser with ingredients, instructions, and nutritional info
 */
export default function RecipeLibrary({ planId }) {
  const [meals, setMeals] = useState([]);
  const [filteredMeals, setFilteredMeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMeal, setSelectedMeal] = useState(null);

  useEffect(() => {
    if (planId) {
      loadMeals();
    }
  }, [planId]);

  useEffect(() => {
    filterMeals();
  }, [meals, searchTerm]);

  const loadMeals = async () => {
    setIsLoading(true);
    try {
      const result = await planService.getMeals(planId);
      if (result.success) {
        // Get unique meals (recipes) - filter by meals that have recipe-like data
        const uniqueMeals = result.data.filter(
          (m) => m.instructions || (m.ingredients && m.ingredients.length > 0)
        );
        setMeals(uniqueMeals);
      }
    } catch (error) {
      console.error('Error loading meals:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterMeals = () => {
    let filtered = [...meals];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.meal_name?.toLowerCase().includes(term) ||
          m.description?.toLowerCase().includes(term)
      );
    }

    setFilteredMeals(filtered);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading recipes...</p>
        </div>
      </div>
    );
  }

  if (selectedMeal) {
    return <RecipeDetail meal={selectedMeal} onBack={() => setSelectedMeal(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Recipe Library</h2>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search recipes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Recipe Grid */}
      {filteredMeals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMeals.map((meal) => (
            <motion.div
              key={meal.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => setSelectedMeal(meal)}
            >
              {/* Recipe Image */}
              <div className="h-48 bg-gray-100 relative">
                {meal.image_url ? (
                  <img
                    src={meal.image_url}
                    alt={meal.meal_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <ImageIcon className="w-16 h-16 text-gray-300" />
                  </div>
                )}
              </div>

              <div className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{meal.meal_name}</h3>
                {meal.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{meal.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  {meal.prep_time_minutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{meal.prep_time_minutes} min</span>
                    </div>
                  )}
                  {meal.macros?.calories && (
                    <div className="flex items-center gap-1">
                      <UtensilsCrossed className="w-4 h-4" />
                      <span>{meal.macros.calories} cal</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <UtensilsCrossed className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No recipes found</h3>
          <p className="text-gray-600">
            {searchTerm ? 'Try adjusting your search' : 'No recipes available for this plan.'}
          </p>
        </div>
      )}
    </div>
  );
}

// Recipe Detail Component
function RecipeDetail({ meal, onBack }) {
  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        ← Back to Library
      </button>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{meal.meal_name}</h1>
        {meal.description && <p className="text-gray-700 mb-6">{meal.description}</p>}

        {/* Image */}
        {meal.image_url && (
          <div className="mb-6">
            <img
              src={meal.image_url}
              alt={meal.meal_name}
              className="w-full rounded-lg"
            />
          </div>
        )}

        {/* Macros */}
        {meal.macros && Object.keys(meal.macros).length > 0 && (
          <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            {meal.macros.calories && (
              <div>
                <p className="text-xs text-gray-600 mb-1">Calories</p>
                <p className="text-xl font-bold text-gray-900">{meal.macros.calories}</p>
              </div>
            )}
            {meal.macros.protein && (
              <div>
                <p className="text-xs text-gray-600 mb-1">Protein</p>
                <p className="text-xl font-bold text-gray-900">{meal.macros.protein}g</p>
              </div>
            )}
            {meal.macros.carbs && (
              <div>
                <p className="text-xs text-gray-600 mb-1">Carbs</p>
                <p className="text-xl font-bold text-gray-900">{meal.macros.carbs}g</p>
              </div>
            )}
            {meal.macros.fats && (
              <div>
                <p className="text-xs text-gray-600 mb-1">Fats</p>
                <p className="text-xl font-bold text-gray-900">{meal.macros.fats}g</p>
              </div>
            )}
          </div>
        )}

        {/* Prep/Cook Time */}
        {(meal.prep_time_minutes || meal.cook_time_minutes) && (
          <div className="flex items-center gap-6 mb-6">
            {meal.prep_time_minutes && (
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-600">Prep Time</p>
                  <p className="font-medium text-gray-900">{meal.prep_time_minutes} minutes</p>
                </div>
              </div>
            )}
            {meal.cook_time_minutes && (
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-600">Cook Time</p>
                  <p className="font-medium text-gray-900">{meal.cook_time_minutes} minutes</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Serving Size */}
        {meal.serving_size && (
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-400" />
              <p className="text-sm text-gray-600">
                Serving Size: <span className="font-medium text-gray-900">{meal.serving_size}</span>
              </p>
            </div>
          </div>
        )}

        {/* Ingredients */}
        {meal.ingredients && meal.ingredients.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Ingredients</h2>
            <ul className="space-y-2">
              {meal.ingredients.map((ingredient, idx) => (
                <li key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <span className="text-gray-700">
                    {typeof ingredient === 'string'
                      ? ingredient
                      : `${ingredient.amount || ''} ${ingredient.unit || ''} ${ingredient.name || ''}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Instructions */}
        {meal.instructions && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Instructions</h2>
            <div className="prose max-w-none">
              <p className="text-gray-700 whitespace-pre-line">{meal.instructions}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
